import React, { useState, useEffect, useMemo } from 'react';
import { Download, Eye, Trash2, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../api';
import Pagination from '../Common/Pagination';

interface DeletedProject {
  id: number;
  project_name: string;
  deleted_at: string;
  user_id: number | null;
  downloaded_by_user: boolean;
  deleted_from_user_view: boolean;
  admin_view_count: number;
  certificate_id: string;
  user?: {
    id: number;
    email: string;
    name: string;
  } | null;
}

const DeletedProjectsAdmin: React.FC = () => {
  const [deletedProjects, setDeletedProjects] = useState<DeletedProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState<number | null>(null);
  const [viewing, setViewing] = useState<number | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Calculate pagination
  const totalPages = Math.ceil(deletedProjects.length / itemsPerPage);
  const paginatedProjects = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return deletedProjects.slice(startIndex, endIndex);
  }, [deletedProjects, currentPage]);

  useEffect(() => {
    fetchDeletedProjects();
  }, []);

  const fetchDeletedProjects = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/admin/deleted-projects');
      setDeletedProjects(response.data.deletedProjects || []);
    } catch (error) {
      console.error('Failed to fetch deleted projects:', error);
      toast.error('Failed to load deleted projects');
    } finally {
      setLoading(false);
    }
  };

  const viewPdfPreview = async (project: DeletedProject) => {
    setViewing(project.id);
    try {
      const response = await api.post(
        `/api/admin/deleted-projects/${project.id}/certificate`,
        {},
        { responseType: 'blob' }
      );

      // Create blob URL for PDF preview
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      setPdfUrl(url);

      // Update view count in local state
      setDeletedProjects(prev =>
        prev.map(p =>
          p.id === project.id
            ? { ...p, admin_view_count: p.admin_view_count + 1 }
            : p
        )
      );

      toast.success('PDF loaded successfully');
    } catch (error) {
      console.error('Error viewing PDF:', error);
      toast.error('Failed to load PDF');
    } finally {
      setViewing(null);
    }
  };

  const closePdfPreview = () => {
    if (pdfUrl) {
      window.URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }
  };

  const downloadCertificate = async (project: DeletedProject) => {
    setDownloading(project.id);
    try {
      const response = await api.post(
        `/api/admin/deleted-projects/${project.id}/certificate`,
        {},
        { responseType: 'blob' }
      );

      // Create blob and download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `purge-certificate-${project.project_name.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Certificate downloaded successfully');

      // Update view count in local state
      setDeletedProjects(prev =>
        prev.map(p =>
          p.id === project.id
            ? { ...p, admin_view_count: p.admin_view_count + 1 }
            : p
        )
      );
    } catch (error) {
      console.error('Error downloading certificate:', error);
      toast.error('Failed to download certificate');
    } finally {
      setDownloading(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-electric-blue" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Auto-Deleted Projects</h2>
          <p className="text-sm text-gray-600 mt-1">
           These Projects are automatically deleted after expiry date. You can view and download purge certificates.
          </p>
        </div>

      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <Trash2 className="w-5 h-5 text-red-500" />
            <span className="text-sm font-medium text-gray-600">Total Auto-Deleted Projects</span>
          </div>
          <p className="text-2xl font-bold text-gray-900 mt-2">{deletedProjects.length}</p>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <Download className="w-5 h-5 text-green-500" />
            <span className="text-sm font-medium text-gray-600">Total Purge Certificate Downloaded</span>
          </div>
          <p className="text-2xl font-bold text-gray-900 mt-2">
            {deletedProjects.filter(p => p.downloaded_by_user).length}
          </p>
        </div>

      </div>

      {/* Projects List */}
      {deletedProjects.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-lg p-12 text-center">
          <Trash2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Deleted Projects</h3>
          <p className="text-sm text-gray-600">
            There are no deleted projects in the system yet.
          </p>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Project Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Deleted Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedProjects.map((project) => (
                <tr key={project.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{project.project_name}</div>
                    <div className="text-xs text-gray-500">{project.certificate_id}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {project.user?.name || 'Unknown'}
                    </div>
                    <div className="text-xs text-gray-500">
                      {project.user?.email || 'N/A'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(project.deleted_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {project.downloaded_by_user ? (
                      <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                        Purge Certificate Downloaded
                      </span>
                    ) : (
                      <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded-full">
                       Purge Certificate Not Downloaded
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => viewPdfPreview(project)}
                        disabled={viewing === project.id}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50"
                        title="View PDF"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => downloadCertificate(project)}
                        disabled={downloading === project.id}
                        className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors disabled:opacity-50"
                        title="Download Certificate"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          {deletedProjects.length > 0 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={deletedProjects.length}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
              itemName="projects"
            />
          )}
        </div>
      )}

      {/* PDF Preview Modal */}
      {pdfUrl && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-6xl h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-xl font-bold text-gray-900">Purge Certificate Preview</h3>
              <button
                onClick={closePdfPreview}
                className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
              >
                ✕
              </button>
            </div>
            <div className="flex-1 overflow-hidden">
              <iframe
                src={pdfUrl}
                className="w-full h-full"
                title="PDF Preview"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeletedProjectsAdmin;
